import useLogger from 'hooks/useLogger';
import type { TickerData, TickerModification } from './Ticker.interface';

type LoggerType = ReturnType<typeof useLogger>;

export function handleAdd(
  data: TickerData[],
  modification: TickerModification,
  defaultBackgroundColor: string,
  defaultForegroundColor: string,
  logger: LoggerType,
): TickerData[] {
  const {
    position,
    link,
    topText,
    bottomText,
    linkTitle,
    backgroundColor,
    foregroundColor,
    dataEm,
  } = modification;
  if (position !== undefined && position >= 0 && position <= data.length) {
    const newTicker: TickerData = {
      link: link ?? '',
      topText: topText ?? '',
      bottomText: bottomText ?? '',
      linkTitle: linkTitle ?? '',
      backgroundColor: backgroundColor ?? defaultBackgroundColor,
      foregroundColor: foregroundColor ?? defaultForegroundColor,
      dataEm,
    };
    return [...data.slice(0, position), newTicker, ...data.slice(position)];
  }
  logger.warn('Invalid position for add action:', String(position));
  return data;
}

export function handleRemove(
  data: TickerData[],
  modification: TickerModification,
  logger: LoggerType,
): TickerData[] {
  const { position } = modification;
  if (position !== undefined && position >= 0 && position < data.length) {
    return data.filter((_, idx) => idx !== position);
  }
  logger.warn('Invalid position for remove action:', String(position));
  return data;
}

export function handleUpdate(
  data: TickerData[],
  modification: TickerModification,
  logger: LoggerType,
): TickerData[] {
  const {
    position,
    link,
    topText,
    bottomText,
    linkTitle,
    backgroundColor,
    foregroundColor,
    dataEm,
  } = modification;
  if (position !== undefined && position >= 0 && position < data.length) {
    return data.map((item, idx) =>
      idx === position
        ? {
            link: link ?? item.link,
            topText: topText ?? item.topText,
            bottomText: bottomText ?? item.bottomText,
            linkTitle: linkTitle ?? item.linkTitle,
            backgroundColor: backgroundColor ?? item.backgroundColor,
            foregroundColor: foregroundColor ?? item.foregroundColor,
            dataEm,
          }
        : item,
    );
  }
  logger.warn('Invalid position for update action:', String(position));
  return data;
}

export const generateDataEm = (ticker: TickerData) => {
  return (
    ticker.dataEm ??
    `tkr_ticker_1_ticker-1_na_naDetails_${ticker.topText.replace(
      // eslint-disable-next-line no-useless-escape
      /[.,\/#!$%\^&\*;:'{}=\-_`~()\s]/g,
      '',
    )}`
  );
};

export function applyModification(
  data: TickerData[],
  modification: TickerModification,
  defaultBackgroundColor: string,
  defaultForegroundColor: string,
  logger: LoggerType,
): TickerData[] {
  switch (modification.action) {
    case 'add':
      return handleAdd(data, modification, defaultBackgroundColor, defaultForegroundColor, logger);
    case 'remove':
      return handleRemove(data, modification, logger);
    case 'update':
      return handleUpdate(data, modification, logger);
    default:
      logger.warn('Invalid action type in modifications:', modification.action);
      return data;
  }
}
